<script setup lang="ts">
import BrandMark from '@shared/ui/components/BrandMark.vue';
import GoogleLogo from '@shared/ui/icons/GoogleLogo.vue';
import AppleLogo from '@shared/ui/icons/AppleLogo.vue';
import EmailLogin from '@features/auth/components/EmailLogin.vue';
import MicrosoftLogo from '@shared/ui/icons/MicrosoftLogo.vue';
import UPMLogo from '@shared/ui/icons/UPMLogo.vue';
import { useAuthStore } from '@/stores';
import { computed, ref, onMounted } from 'vue';
import { AuthRequest } from '@shared/api';
import ButtonComponent from '@shared/ui/components/ButtonComponent.vue';
import { handleAxiosError } from '@shared/utils/error.ts';
import { useTenantStore } from '@features/tenant/store/tenant';
import { launchExternalSsoLogin } from '@features/auth/externalSsoLogin';
import { startCentralSso } from '@features/auth/startCentralSso';

const authStore = useAuthStore();

const isLoading = ref(false);
const loginData = {} as AuthRequest;
const emailError = ref(false);
// TODO: remove comment when email login is added again, requested by https://legendaryum.atlassian.net/browse/LE-1161
const emailErrorMessage = ref('');
const credentialsError = ref(false);
const credentialsErrorMessage = ref('');
const microsoftAuthUrl = ref('');
const upmAuthUrl = ref('');
const emailLogin = ref(false);

const emit = defineEmits<{
  'change:view': [value: 'login' | 'register' | 'recovery'];
  'close:modal': [];
}>();

const allowMicrosoftSSO = computed(
  () =>
    import.meta.env.VITE_ENV === 'dev' ||
    import.meta.env.VITE_ENV === 'staging',
);

const tenantStore = useTenantStore();

const offers = (provider: string) =>
  tenantStore.isDelegated
    ? provider === 'external_sso'
    : tenantStore.supports(provider);

const allowEmailLogin = computed(
  () => import.meta.env.VITE_ENV === 'dev' && !tenantStore.isDelegated,
);

const externalSsoLoginUrl = computed(() => tenantStore.externalSsoLoginUrl);

onMounted(async () => {
  try {
    const { microsoft, upm } = await authStore.getAuthUrl();
    microsoftAuthUrl.value = microsoft;
    upmAuthUrl.value = upm;
  } catch (error) {
    handleAxiosError(error, true);
  }
});

const microsoftAuth = () => {
  window.location.href = microsoftAuthUrl.value;
};

const upmAuth = () => {
  window.location.href = upmAuthUrl.value;
};

const externalSsoAuth = () => {
  launchExternalSsoLogin(externalSsoLoginUrl.value, (loginUrl) => {
    window.location.assign(loginUrl);
  });
};

const appleAuth = () => {
  isLoading.value = true;
  startCentralSso('apple');
};

const googleAuth = () => {
  isLoading.value = true;
  startCentralSso('google');
};

const redirectEmailLogin = () => {
  if (emailError.value) return;
  emailLogin.value = true;
};
</script>

<template>
  <EmailLogin
    v-if="emailLogin"
    :email="loginData.email"
    @back="emailLogin = false"
    @recovery="emit('change:view', 'recovery')"
    @on:close="
      () => {
        emailLogin = false;
        emit('close:modal');
      }
    "
  />
  <form v-else @submit.prevent="redirectEmailLogin">
    <div class="grid grid-cols-1">
      <div class="flex flex-col">
        <div class="grid grid-cols-1">
          <div class="flex flex-col justify-center">
            <div class="mb-4 flex flex-col items-center justify-center">
              <BrandMark
                mark-class="h-16 w-16"
                name-class="text-2xl font-bold text-white"
              />
            </div>
          </div>
        </div>
        <div class="grid grid-cols-1">
          <div class="flex flex-col">
            <p class="text-md text-center">
              Elige tu cuenta para iniciar sesión
            </p>
            <div
              v-if="allowEmailLogin"
              class="relative my-3 mb-5 flex flex-col"
            >
              <label for="email" class="mb-1 block text-sm">Email</label>
              <input
                v-model="loginData.email"
                name="email"
                type="email"
                class="h-9 w-full rounded-md border-2 p-4"
                :class="{
                  'border-2 bg-white text-black': !emailError,
                  'border-2 border-red-500 bg-white text-red-700': emailError,
                }"
                placeholder="email@email.com"
                required
                maxlength="50"
              />
              <p
                v-if="emailError"
                class="absolute right-0 top-16 text-right text-xs italic"
              >
                {{ emailErrorMessage }}
              </p>
              <div class="flex items-center justify-center">
                <ButtonComponent type="submit" class="mt-6">
                  Continuar
                </ButtonComponent>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-6 flex flex-col items-center justify-center gap-4">
          <button
            v-if="offers('google')"
            type="button"
            class="flex w-full cursor-pointer items-center justify-center gap-4 rounded-lg bg-white p-2 text-black transition-all hover:bg-white/85"
            @click="googleAuth"
          >
            <GoogleLogo class="size-5" />
            <div class="w-50 text-left">
              <span class="select-none">Iniciar sesión con Google</span>
            </div>
          </button>
          <button
            v-if="offers('apple')"
            type="button"
            class="flex w-full cursor-pointer items-center justify-center gap-4 rounded-lg bg-white p-2 text-black transition-all hover:bg-white/85"
            @click="appleAuth"
          >
            <AppleLogo class="size-5" />
            <div class="w-50 text-left">
              <span class="select-none">Iniciar sesión con Apple</span>
            </div>
          </button>
          <button
            v-if="externalSsoLoginUrl"
            data-testid="external-sso-login"
            type="button"
            aria-label="Iniciar sesión"
            class="flex w-full cursor-pointer items-center justify-center gap-4 rounded-lg bg-white p-2 text-black transition-all hover:bg-white/85"
            @click="externalSsoAuth"
          >
            <div class="w-50 text-left">
              <span class="select-none">Iniciar sesión</span>
            </div>
          </button>
          <button
            v-if="allowMicrosoftSSO && offers('microsoft')"
            type="button"
            class="flex w-full cursor-pointer items-center justify-center gap-4 rounded-lg bg-white p-2 text-black transition-all hover:bg-white/85"
            @click="microsoftAuth"
          >
            <MicrosoftLogo class="size-5" />
            <div class="w-50 text-left">
              <span class="select-none">Iniciar sesión con Microsoft</span>
            </div>
          </button>
          <button
            v-if="allowMicrosoftSSO && offers('upm')"
            type="button"
            class="flex w-full cursor-pointer items-center justify-center gap-4 rounded-lg bg-white p-2 text-black transition-all hover:bg-white/85"
            @click="upmAuth"
          >
            <UPMLogo class="size-5" />
            <div class="w-50 text-left">
              <span class="select-none">Iniciar sesión con UPM</span>
            </div>
          </button>
        </div>
      </div>
      <div class="mt-6 flex items-center justify-center">
        <div
          v-if="credentialsError"
          class="w-full rounded-md bg-[#50191d] px-3 py-1 text-center text-[#f88f96]"
        >
          <p class="text-center">
            {{ credentialsErrorMessage }}
          </p>
        </div>
      </div>
    </div>
  </form>
</template>

<style scoped></style>
